import "./styles.scss";

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import moment from "moment";

import { useInvoices } from "context/client/invoices";

import useResponsive from "libs/helpers/useResponsive";
import usePagination from "libs/helpers/usePagination";
import { INVOICES_PER_PAGE } from "libs/mobile_settings";
import { invoices_sorting } from "libs/sorting/invoices-sorting";

import Button from "components/Button";
import Select from "components/form/Select";
import Loader from "components/Loader";
import Pagination from "components/Pagination";
import ScrollTopMobile from "components/ScrollTopMobile";
import PlanTag from "../PlanTag";
import InvoiceFilters from "../InvoiceFilters";
import ModalFilters from "./ModalFilters";

import { ReactComponent as ViewIcon } from "assets/icons/new/view.svg";
import { ReactComponent as NoDataIcon } from "assets/icons/no-data.svg";

const mainClass = "invoices-tiles";

const InvoicesTiles = () => {
  const { t, i18n } = useTranslation();
  const { max } = useResponsive();
  const { data, loading, filters, filterHandler, clearAllFilter, showInvoice } =
    useInvoices();
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const perPage = max.sm ? INVOICES_PER_PAGE.mobile : INVOICES_PER_PAGE.default;
  const { currentPage, totalPages, handlePageChange, paginatedData } =
    usePagination(data, perPage);
  const hasFilters = ["website", "start_date", "end_date"].some(
    (name) => !!filters[name],
  );

  useEffect(() => {
    if (!max.sm) setIsMobileFiltersOpen(false);
  }, [max.sm]);

  return (
    <div className={mainClass}>
      <div className={`${mainClass}__header`}>
        <div className={`${mainClass}__filters`}>
          {max.sm ? (
            <Button
              variant="transparent"
              onClick={() => setIsMobileFiltersOpen(true)}
            >
              {t("Filter results")}
            </Button>
          ) : (
            <InvoiceFilters values={filters} onChange={filterHandler} />
          )}
          {max.sm && (
            <Button disabled={!hasFilters} onClick={clearAllFilter}>
              {t("Clear filters")}
            </Button>
          )}
        </div>
        <div className={`${mainClass}__sort`}>
          <Select.Sort
            key={filters?.sort || "default"}
            initial={filters?.sort}
            options={invoices_sorting}
            forceBottom
            onChange={(option) => filterHandler("sort", option.value)}
          />
        </div>
      </div>
      <AnimatePresence initial={false} mode="wait">
        <motion.div
          key={currentPage}
          className={`${mainClass}__row`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ type: "tween", duration: 0.3 }}
        >
          {loading ? (
            <div className={`${mainClass}__loader`}>
              <Loader color="blue" center size="large" />
            </div>
          ) : paginatedData.length > 0 ? (
            paginatedData.map(
              ({
                invoice_id,
                website_id,
                website,
                created_at,
                name,
                amount,
                currency,
              }) => (
                <div className={`${mainClass}__tile`} key={invoice_id}>
                  <div className={`${mainClass}__tile-header`}>
                    <time dateTime={created_at}>
                      {moment(created_at).format("LL")}
                    </time>
                    <Link to={`/websites/${website_id}/`}>{website}</Link>
                  </div>
                  <div className={`${mainClass}__tile-summary`}>
                    <PlanTag name={name} label={`${t("Plan")} ${name}`} />
                    <strong>
                      {new Intl.NumberFormat(i18n.language, {
                        style: "currency",
                        currency: currency.toUpperCase(),
                      }).format(amount / 100)}
                    </strong>
                  </div>
                  <button
                    type="button"
                    className={`${mainClass}__tile-action`}
                    onClick={() => showInvoice(invoice_id)}
                  >
                    {t("View details")}
                    <ViewIcon aria-hidden="true" />
                  </button>
                </div>
              ),
            )
          ) : (
            <div className={`${mainClass}__no-data`}>
              <NoDataIcon aria-hidden="true" />
              <p>{t("No data")}</p>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
      {totalPages > 1 && !loading && (
        <div className={`${mainClass}__pagination`}>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onChange={handlePageChange}
          />
        </div>
      )}
      <div className={`${mainClass}__scroll`}>
        <ScrollTopMobile />
      </div>
      {max.sm && (
        <ModalFilters
          isOpen={isMobileFiltersOpen}
          onClose={() => setIsMobileFiltersOpen(false)}
        />
      )}
    </div>
  );
};

export default InvoicesTiles;
